// @ts-nocheck
import documents_treat from "includes/documents_treat";
import formatDate from "includes/formatDate";
import { SERV_URL } from "includes/paths";
import genRequest from "includes/request";

/** Expiry date (ISO) read from the `exp` claim of the JWT, like v1's `.expires` */
function tokenExpiry(jwt) {
	try {
		const b64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
		const { exp } = JSON.parse(atob(b64));
		return exp ? new Date(exp * 1000).toISOString() : null;
	} catch (e) {
		return null;
	}
}

class AccountObject {
	#employeeId = null;
	#firstName = "";
	#lastName = "";
	#sex = null;
	#hiredDate = null;
	#workplace = null;
	#login = null;
	#telephone = null;
	#mail = null;
	#siteId = null;
	#aspNetUserId = null;
	#companyId = null;
	#companyRankId = null;
	#workFamillyId = null;
	#workFunctionId = null;
	#lastChanged = null;
	#firstConnexion = null;
	#lastConnexion = null;
	#hobby = null;
	#lastChangeAuthorId = null;
	#tokenRegeneration = null;
	#displayPhone = true;
	#displayMail = true;
	#bypassModeration = null;
	#role = "UserPublisher";
	#userTemplateId = null;
	#languageId = null;
	#isActive = null;

	#company = false;
	#companyBackground = false;
	#companyLogo = false;

	#settings = {};

	#token = false;
	#image = false;

	constructor(obj) {
		this.#token = obj?.token ? obj.token : false;
		this.#company = obj?.company ? obj.company : false;
		this.#companyBackground = obj?.companyBackground
			? obj.companyBackground
			: false;
		this.#companyLogo = obj?.companyLogo ? obj.companyLogo : false;
		this.#settings = obj?.settings ? obj.settings : {};
		this.#initValues(obj);
	}

	#initValues(obj) {
		let date = formatDate();

		// /me answers `id`; saved accounts carry `employeeId`: aliased once, here
		this.#employeeId = obj?.id ?? obj?.employeeId ?? null;
		this.#firstName = obj?.firstName ? obj.firstName : "";
		this.#lastName = obj?.lastName ? obj.lastName : "";
		this.#sex = obj?.sex ?? "";
		this.#hiredDate = obj?.hiredDate ? obj.hiredDate : null;
		this.#workplace = obj?.workplace ? obj.workplace : "";
		// `employees.login` is empty in v2: the e-mail is the login identifier
		this.#login = obj?.login || obj?.mail || "";
		this.#telephone = obj?.telephone ?? "";
		this.#mail = obj?.mail ? obj.mail : "";
		this.#siteId = obj?.siteId ? obj.siteId : null;
		this.#aspNetUserId = obj?.aspNetUserId ? obj.aspNetUserId : null;
		this.#companyId = obj?.companyId ? obj.companyId : null;
		this.#companyRankId = obj?.companyRankId ? obj.companyRankId : 1;
		this.#workFamillyId = obj?.workFamilyId ?? null;
		this.#workFunctionId = obj?.workFunctionId
			? obj.workFunctionId
			: null;
		this.#lastChanged = obj?.lastChanged ? obj.lastChanged : date;
		this.#firstConnexion = obj?.firstConnexion
			? obj.firstConnexion
			: null;
		this.#lastConnexion = obj?.lastConnexion ? obj.lastConnexion : null;
		this.#hobby = obj?.hobby ? obj.hobby : "";
		this.#lastChangeAuthorId = obj?.lastChangeAuthorId
			? obj.lastChangeAuthorId
			: null;
		this.#tokenRegeneration = obj?.tokenRegeneration
			? obj.tokenRegeneration
			: false;
		this.#displayPhone =
			obj?.displayPhone !== undefined ? obj.displayPhone : true;
		this.#displayMail =
			obj?.displayMail !== undefined ? obj.displayMail : true;
		this.#bypassModeration =
			obj?.bypassModeration !== undefined
				? obj.bypassModeration
				: false;
		this.#role = obj?.role ? obj.role : "UserPublisher";
		this.#userTemplateId = obj?.userTemplateId
			? obj.userTemplateId
			: null;
		this.#languageId = obj?.languageId ? obj.languageId : 1;
		this.#isActive =
			obj?.isActive !== undefined ? obj.isActive : true;
	}

	/*
	 ** -- Log
	 */

	connect(username, password) {
		let login = username || this.#login;
		if (!this.#token || (login && password))
			return this.passwordConnect(login, password);
	}

	passwordConnect(username, password) {
		let that = this;

		this.#token = false;
		let prom = genRequest(
			"auth/login",
			{ email: username, password },
			"post",
			{ timeout: 15000 }
		).then((resp) => {
			// Normalize NestJS response to legacy token format
			that.#token = {
				access_token: resp.accessToken,
				refresh_token: resp.refreshToken,
				expires_in: resp.expiresIn,
				expires: tokenExpiry(resp.accessToken),
			};
			// remember the identifier typed so saved accounts can be matched
			if (!that.#login) that.#login = username;
			return that.#token;
		});
		return prom;
	}

	/*
	 ** -- Fetch
	 */

	fetch(save_pass) {
		let prom = new Promise((resolve, reject) => {
			this.fetchInfos(save_pass)
				.then(() => {
					return this.fetchCompany();
				}, reject)
				.then(() => resolve(this.values()));
		});
		return prom;
	}

	fetchInfos(save_pass) {
		let that = this;
		let req = Promise.all([
			genRequest("me"),
			genRequest("me/rights").catch(() => [])
		]).then(([resp, rightsResp]) => {
			const raw = Array.isArray(resp) ? resp[0] : resp;
			if (raw) {
				raw.rights = rightsResp;
				that.#initValues(raw);
			} else {
				if (save_pass) {
					this.#token = false;
				}
				throw new Error("Failed retrieve user");
			}
			return raw;
		});
		return req;
	}

	fetchCompany() {
		let prom = new Promise((resolve, reject) => {
			this.fetchCompanyInfos()
				.then(() => {
					return this.fetchImages();
				}, reject)
				.then(resolve, reject);
		});
		return prom;
	}

	fetchCompanyInfos() {
		let that = this;
		let req = genRequest("companies/" + this.#companyId).then(
			(resp) => {
				that.#company = {
					companyId: resp.id,
					name: resp.name,
				};
			}
		);
		return req;
	}

	fetchImages() {
		let that = this;
		let proms = [
			this.fetchCompanyImage(10).then((resp) => {
				that.#companyLogo = resp;
			}),
			this.fetchCompanyImage(12).then((resp) => {
				that.#companyBackground = resp;
			}),
			this.fetchProfilePicture().then((resp) => {
				that.#image = resp;
			}),
		];
		return Promise.allSettled(proms);
	}

	fetchCompanyImage(typeId) {
		if (!this.#companyId || !typeId) return false;
		let req = genRequest(
			"documents/company/" +
				this.#companyId +
				"/type/" +
				typeId
		).then((resp) => {
			let img = documents_treat(resp)[0];
			if (!img) return false;
			return {
				urlOrigin: img.urlOrigin,
				urlMobile: img.urlMobile,
				urlMin: img.urlMin,
			};
		});
		return req;
	}

	fetchProfilePicture() {
		if (!this.#employeeId) return false;
		let req = genRequest(
			"documents/employee/" + this.#employeeId + "/type/2"
		).then((resp) => {
			let img = documents_treat(resp)[0];
			if (!img) return false;
			return {
				urlOrigin: img.urlOrigin,
				urlMobile: img.urlMobile,
				urlMin: img.urlMin,
				urlWeb: img.urlWeb,
			};
		});
		return req;
	}

	isTokenValid() {
		if (!this.#token) return false;
		// tokens saved before `expires` existed: read the expiry from the JWT itself
		let expires = this.#token.expires ?? tokenExpiry(this.#token.access_token);
		if (!expires) return false;
		let date = new Date();
		let exp = new Date(expires);
		return date < exp;
	}

	/*
	 ** -- GET
	 */

	// defaults

	get employeeId() {
		return this.#employeeId;
	}

	get firstName() {
		return this.#firstName;
	}

	get lastName() {
		return this.#lastName;
	}

	get login() {
		return this.#login;
	}

	get siteId() {
		return this.#siteId;
	}

	get companyId() {
		return this.#companyId;
	}

	get firstConnexion() {
		return this.#firstConnexion;
	}

	get lastConnexion() {
		return this.#lastConnexion;
	}

	get tokenRegeneration() {
		return this.#tokenRegeneration;
	}

	get languageId() {
		return this.#languageId;
	}

	// customs

	get image() {
		return this.#image;
	}

	get companyBackground() {
		return this.#companyBackground;
	}
	get companyLogo() {
		return this.#companyLogo;
	}

	get token() {
		return this.#token;
	}

	values(with_token) {
		let vals = {
			employeeId: this.#employeeId,
			firstName: this.#firstName,
			lastName: this.#lastName,
			sex: this.#sex,
			hiredDate: this.#hiredDate,
			workplace: this.#workplace,
			login: this.#login,
			telephone: this.#telephone,
			mail: this.#mail,
			siteId: this.#siteId,
			aspNetUserId: this.#aspNetUserId,
			companyId: this.#companyId,
			companyRankId: this.#companyRankId,
			workFamilyId: this.#workFamillyId,
			workFunctionId: this.#workFunctionId,
			lastChanged: this.#lastChanged,
			firstConnexion: this.#firstConnexion,
			lastConnexion: this.#lastConnexion,
			hobby: this.#hobby,
			lastChangeAuthorId: this.#lastChangeAuthorId,
			tokenRegeneration: this.#tokenRegeneration,
			displayPhone: this.#displayPhone,
			displayMail: this.#displayMail,
			bypassModeration: this.#bypassModeration,
			role: this.#role,
			userTemplateId: this.#userTemplateId,
			languageId: this.#languageId,
			isActive: this.#isActive,

			image: this.#image,
			companyBackground: this.#companyBackground,
			companyLogo: this.#companyLogo,
			company: this.#company,

			settings: this.#settings,
		};

		if (with_token) vals.token = this.#token;
		return vals;
	}
}

export default AccountObject;
